// spzWorkerPool.ts — a small pool of spzTranscode.worker workers.
//
// Each worker imports @sparkjsdev/spark once and converts .ply → .spz on demand;
// the pool dispatches queued frames to whichever worker is idle, so transcoding
// runs in parallel across cores (and off the UI thread). Used by ModelUploader
// to speed up sequence uploads.

type Resolver = { resolve: (u: Uint8Array) => void; reject: (e: Error) => void }
type PoolJob = { id: number } & Resolver & { plyBytes: Uint8Array; name?: string; opts?: Record<string, unknown> }

export class SpzWorkerPool {
  private workers: Worker[] = []
  private idle: Worker[] = []
  private queue: PoolJob[] = []
  private pending = new Map<number, Resolver>()
  private busyId = new WeakMap<Worker, number>()
  private seq = 0

  constructor(size: number) {
    for (let i = 0; i < size; i++) {
      const w = new Worker(new URL('./spzTranscode.worker.ts', import.meta.url), { type: 'module' })
      w.onmessage = (e: MessageEvent) => this.onDone(w, e.data)
      w.onerror = () => this.onCrash(w)
      this.workers.push(w)
      this.idle.push(w)
    }
  }

  private onDone(w: Worker, data: any) {
    const p = this.pending.get(data.id)
    if (p) {
      this.pending.delete(data.id)
      if (data.ok) p.resolve(new Uint8Array(data.spz as ArrayBuffer))
      else p.reject(new Error(data.error || 'transcode failed'))
    }
    this.busyId.delete(w)
    this.idle.push(w)
    this.pump()
  }

  private onCrash(w: Worker) {
    const id = this.busyId.get(w)
    if (id !== undefined) {
      const p = this.pending.get(id)
      if (p) {
        this.pending.delete(id)
        p.reject(new Error('spz transcode worker crashed'))
      }
      this.busyId.delete(w)
    }
    // The crashed worker is not returned to the idle set; remaining workers carry on.
    this.pump()
  }

  private pump() {
    while (this.idle.length && this.queue.length) {
      const w = this.idle.pop()!
      const job = this.queue.shift()!
      this.pending.set(job.id, { resolve: job.resolve, reject: job.reject })
      this.busyId.set(w, job.id)
      // Caller hands us an owned array — transfer its buffer (zero-copy).
      w.postMessage(
        { id: job.id, plyBytes: job.plyBytes.buffer, name: job.name, opts: job.opts },
        [job.plyBytes.buffer],
      )
    }
  }

  /**
   * Transcode a .ply frame to .spz. Takes OWNERSHIP of `plyBytes` (its buffer is
   * transferred to a worker, so don't reuse it after calling).
   */
  transcode(
    plyBytes: Uint8Array,
    opts?: { name?: string; transcode?: Record<string, unknown> },
  ): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
      this.queue.push({
        id: this.seq++, plyBytes, name: opts?.name, opts: opts?.transcode, resolve, reject,
      })
      this.pump()
    })
  }

  dispose() {
    for (const w of this.workers) w.terminate()
    this.workers = []
    this.idle = []
    this.queue = []
    this.pending.clear()
  }
}
